import {
  Bar, BarChart, CartesianGrid, ComposedChart, LabelList, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

// Helpers duplicados do MetricsTab de propósito: manter o recharts (e este
// arquivo) fora do bundle inicial — só carrega via lazy() na aba Métricas.
function fmtInt(v: number) {
  return v.toLocaleString("pt-BR");
}

function fmtDay(d: string) {
  return `${d.slice(8, 10)}/${d.slice(5, 7)}`;
}

export interface DailyPoint {
  date: string;
  inbound: number;
  outbound: number;
  ia: number;
  crm: number;
  ia_externa: number;
  api: number;
  aparelho: number;
}

export interface TopPoint { name: string; inbound: number; outbound: number }

export const SOURCE_META: { key: "ia" | "ia_externa" | "crm" | "api" | "aparelho"; label: string; color: string }[] = [
  { key: "ia", label: "IA", color: "var(--brand)" },
  { key: "ia_externa", label: "IA externa", color: "var(--violet)" },
  { key: "crm", label: "CRM", color: "var(--info)" },
  { key: "api", label: "API", color: "var(--warn)" },
  { key: "aparelho", label: "Aparelho", color: "var(--ok)" },
];

const TOOLTIP_STYLE = { background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12 } as const;
const TICK = { fontSize: 11, fill: "var(--ink-muted)" } as const;

type ChartProps =
  | { type: "daily"; data: DailyPoint[] }
  | { type: "sources"; data: DailyPoint[] }
  | { type: "top"; data: TopPoint[] }
  | { type: "buckets"; data: { bucket: string; leads: number }[] };

export default function WabaMetricsChart(props: ChartProps) {
  if (props.type === "daily") {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={props.data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={TICK} stroke="var(--line-strong)" />
          <YAxis allowDecimals={false} tick={TICK} stroke="var(--line-strong)" />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            labelFormatter={(d: unknown) => fmtDay(String(d))}
            formatter={(value: unknown, name: unknown) => [fmtInt(Number(value)), String(name)]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="inbound" name="Recebidas" fill="var(--info)" radius={[3, 3, 0, 0]} maxBarSize={28} />
          <Bar dataKey="outbound" name="Enviadas" fill="var(--brand)" radius={[3, 3, 0, 0]} maxBarSize={28} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  if (props.type === "sources") {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={props.data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={TICK} stroke="var(--line-strong)" />
          <YAxis allowDecimals={false} tick={TICK} stroke="var(--line-strong)" />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            labelFormatter={(d: unknown) => fmtDay(String(d))}
            formatter={(value: unknown, name: unknown) => [fmtInt(Number(value)), String(name)]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {SOURCE_META.map(s => (
            <Bar key={s.key} dataKey={s.key} name={s.label} stackId="src" fill={s.color} maxBarSize={28} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (props.type === "buckets") {
    const total = props.data.reduce((s, b) => s + b.leads, 0);
    const pct = (v: number) =>
      total ? `${((v / total) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%` : "";
    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={props.data} margin={{ top: 18, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket" tick={TICK} stroke="var(--line-strong)" />
          <YAxis allowDecimals={false} tick={TICK} stroke="var(--line-strong)" />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            labelFormatter={(b: unknown) => `${String(b)} mensagens`}
            formatter={(value: unknown) => [`${fmtInt(Number(value))} (${pct(Number(value))})`, "Leads"]}
          />
          <Bar dataKey="leads" name="Leads" fill="var(--brand)" radius={[3, 3, 0, 0]} maxBarSize={48}>
            <LabelList
              dataKey="leads"
              position="top"
              formatter={(v: unknown) => pct(Number(v))}
              style={{ fontSize: 11, fill: "var(--ink-secondary)" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // top: barras horizontais, top 10 conexões por volume
  const height = Math.max(160, props.data.length * 34 + 40);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={props.data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={TICK} stroke="var(--line-strong)" />
        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "var(--ink-secondary)" }} stroke="var(--line-strong)" />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(value: unknown, name: unknown) => [fmtInt(Number(value)), String(name)]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="inbound" name="Recebidas" stackId="t" fill="var(--info)" maxBarSize={18} />
        <Bar dataKey="outbound" name="Enviadas" stackId="t" fill="var(--brand)" radius={[0, 3, 3, 0]} maxBarSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
}
